import { useEffect, useMemo, useState } from "react";
import { Loader2, PackageCheck, Package } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DrawerSection } from "@/components/forms/fields";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { returnsService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { RETURN_CONDITION_META } from "@/lib/constants";

export function ReturnConfirmDrawer({ open, onOpenChange, record, storeName, onSaved }) {
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && record) {
      setLines((record.products || []).map((p) => ({
        sku: p.sku,
        product_name: p.product_name || "",
        quantity: p.quantity || 0,
        confirmed: !!p.confirmed,
        condition: p.condition || "sellable",
        confirmQty: String(p.confirmed ? p.confirmed_qty : p.quantity || 0),
        note: p.note || "",
      })));
    }
  }, [open, record]);

  const setLine = (i, key, value) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));

  const canSave = useMemo(
    () => !saving && lines.some((l) => !l.confirmed && (Number(l.confirmQty) || 0) > 0),
    [lines, saving]
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const products = lines
        .filter((l) => !l.confirmed && (Number(l.confirmQty) || 0) > 0)
        .map((l) => ({ sku: l.sku, condition: l.condition, quantity: Number(l.confirmQty) || 0, note: (l.note || "").trim() }));
      const res = await returnsService.confirm(record.id, products);
      const rt = res?.rt_inventory || [];
      toast.success(`Return confirmed · ${rt.length} RT inventory record(s) created`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={PackageCheck}
      title="Confirm Return"
      description={record ? `${record.return_id} · ${storeName || "—"}` : ""}
      widthClass="sm:max-w-2xl"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="return-confirm-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="return-confirm-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm return"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Warehouse confirmation">
          <p className="text-xs text-muted-foreground">
            Confirm the condition of each returned SKU. <span className="font-medium text-success-dark">Sellable</span> items are restocked into available inventory (RT record). <span className="font-medium text-error-dark">Non-Sellable</span> items are recorded but never added to available stock.
          </p>
          <div className="space-y-3">
            {lines.map((l, i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-3 shadow-soft" data-testid={`return-confirm-line-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-semibold text-foreground">{l.sku}</span>
                    {l.product_name && <span className="text-sm text-muted-foreground">· {l.product_name}</span>}
                  </div>
                  {l.confirmed && <StatusBadge label={RETURN_CONDITION_META[l.condition]?.label || "Confirmed"} tone={RETURN_CONDITION_META[l.condition]?.tone || "green"} />}
                </div>
                <div className="grid grid-cols-12 items-end gap-2">
                  <div className="col-span-3">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Returned</label>
                    <div className="flex h-10 items-center rounded-lg border border-input bg-muted/40 px-3 text-sm tabular-nums text-foreground" data-testid={`return-confirm-qty-expected-${i}`}>{l.quantity}</div>
                  </div>
                  <div className="col-span-4">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Condition</label>
                    <Select value={l.condition} onValueChange={(v) => setLine(i, "condition", v)} disabled={l.confirmed}>
                      <SelectTrigger data-testid={`return-confirm-condition-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sellable">Sellable</SelectItem>
                        <SelectItem value="non_sellable">Non-Sellable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-5">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Confirmed Qty</label>
                    <Input type="number" min={0} value={l.confirmQty} onChange={(e) => setLine(i, "confirmQty", e.target.value)} disabled={l.confirmed} data-testid={`return-confirm-qty-${i}`} />
                  </div>
                </div>
                {!l.confirmed && (
                  <Input className="mt-2" value={l.note} onChange={(e) => setLine(i, "note", e.target.value)} placeholder="Optional note" data-testid={`return-confirm-note-${i}`} />
                )}
              </div>
            ))}
          </div>
        </DrawerSection>
      </div>
    </Drawer>
  );
}
